import { Component, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, AfterViewInit } from "@angular/core";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { Contract } from "@edp-components/edp-contract";
import { getFunciona } from "@edp-store/selectors/dashboard.selectors";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { calculateFunciona, FuncionaPlafond } from "./funciona-service-type.model";
import { TranslateService } from "@ngx-translate/core";
import { Router } from "@angular/router";
import { FormatDecimalValue } from "@edp-utils/format-decimal-value.utils";
import { OperationHistoryService } from "@edp-services/operation-history.service";
import moment from "moment";
import { PackService } from "@edp-services/pack.service";

const colors = {
    "edp-seaweed-green-01": "#225E66",
    "edp-seaweed-green-04": "#91AFB3",
    "edp-neutral-10": "#FFFFFF",
    "edp-error-01": "#E32C2C",
    "edp-marine-blue-01": "#212E3E",
};

const highPercentage = 90;

@Component({
    selector: "edp-widget-funciona",
    templateUrl: "./edp-widget-funciona.component.html",
    styleUrls: ["./edp-widget-funciona.component.scss"],
})
export class EdpWidgetFuncionaComponent implements OnDestroy, AfterViewInit {
    @ViewChild("chartFuncionaElement") chartElement: ElementRef<any>;
    private chart: am4charts.GaugeChart;
    public selectedContract: Contract;
    public mobileSize = false;
    public dataChart = {
        plafondTotal: 0,
        plafondAvailable: 0,
        plafondUsed: 0,
    };
    public plafondCompleted = false;
    public highPercentageUsed = false;
    public isFull = false;
    private unsubscribe = new Subject();

    @HostListener("window:resize")
    public onResize() {
        const backup = this.mobileSize;
        this.mobileSize = false;
        if (window.innerWidth <= SCREEN_SIZES.MD) {
            this.mobileSize = true;
        }
        if (backup !== this.mobileSize) {
            // TO TEST
            this.setDataChart();
            this.chartConstruction();
        }
    }

    constructor(
        private store: Store<IAppState>,
        private translate: TranslateService,
        private router: Router,
        private operationHistoryService: OperationHistoryService,
        private packService: PackService,
    ) {}

    public ngAfterViewInit(): void {
        combineLatest([this.store.select(getContract), this.store.select(getFunciona)]).subscribe(([contract, funciona]) => {
            if (contract && funciona) {
                this.selectedContract = contract;
                this.isFull = !!contract.HasPack?.full;
                this.dataChart = calculateFunciona(funciona, contract);
                this.setDataChart();
                this.chartConstruction();
            }

            return;
        });
    }

    private setDataChart() {
        const percentagePlafondUsed = Math.floor((this.dataChart.plafondUsed * 100) / this.dataChart.plafondTotal);
        this.highPercentageUsed = percentagePlafondUsed >= highPercentage;
        this.plafondCompleted = percentagePlafondUsed === 100;
    }

    private chartConstruction(): void {
        if (this.chartElement && this.dataChart.plafondTotal) {
            this.chart = am4core.create(this.chartElement.nativeElement, am4charts.GaugeChart);
            this.chart.paddingLeft = this.mobileSize ? 0 : 55;
            this.chart.paddingRight = this.mobileSize ? 0 : 55;
            this.chart.paddingBottom = this.mobileSize ? 5 : 10;
            this.chart.paddingTop = 20;
            this.chart.radius = am4core.percent(100);
            this.chart.innerRadius = am4core.percent(85);

            const axis: am4charts.ValueAxis<am4charts.AxisRenderer> = this.chart.xAxes.push(new am4charts.ValueAxis());
            axis.min = 0;
            axis.max = this.dataChart.plafondTotal;
            axis.strictMinMax = true;

            const range0: am4charts.ValueAxisDataItem = axis.axisRanges.create();
            range0.value = 0;
            range0.endValue = 0;
            range0.axisFill.fillOpacity = 1;
            range0.axisFill.fill = am4core.color(colors["edp-seaweed-green-01"]);

            const range1 = axis.axisRanges.create();
            range1.axisFill.fillOpacity = 1;
            range1.axisFill.fill = am4core.color(colors["edp-neutral-10"]);
            range1.endValue = axis.max;

            range0.endValue = this.dataChart.plafondUsed;
            range1.value = this.dataChart.plafondUsed;
            range1.label.text = FormatDecimalValue.getFormatted(this.dataChart.plafondUsed);

            axis.renderer.grid.template.disabled = true;
            axis.renderer.labels.template.fontFamily = "Mulish";
            axis.renderer.labels.template.fontSize = "10px";
            axis.renderer.labels.template.fill = am4core.color(colors["edp-seaweed-green-01"]);

            range0.label.fill = am4core.color(colors["edp-seaweed-green-04"]);
            axis.renderer.labels.template.adapter.add("text", value => {
                if (this.dataChart.plafondUsed > 0 && Number(value) === range1.endValue && !this.plafondCompleted) {
                    return FormatDecimalValue.getFormatted(this.dataChart.plafondTotal);
                } else if (!this.dataChart.plafondUsed && Number(value) === 0) {
                    return FormatDecimalValue.getFormatted(0);
                } else {
                    return "";
                }
            });

            if (this.highPercentageUsed) {
                range0.axisFill.fill = am4core.color(colors["edp-error-01"]);
                axis.renderer.labels.template.fill = am4core.color(colors["edp-error-01"]);
            }
        }
    }

    public goToPacks() {
        this.packService.goToNewPlans(this.selectedContract);
    }

    public goToRequest() {
        this.sendOperationHistoryService("CLICKED_CTA_SUPPORT_DASHBOARD");
        const url = this.translate.instant("pt.edp.edponline.service.funciona.request.assistance.link");
        this.router.navigate([url]);
    }

    private sendOperationHistoryService(key: string) {
        this.operationHistoryService.registerWithContract(this.selectedContract.ContractId, key, null, null, null, false);
    }

    public ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
